import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "text-white" }
const _hoisted_2 = { class: "flex justify-between items-center" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = {
  key: 0,
  class: "mr-2 sm:mr-4 text-xl w-0.5 h-8 bg-white text-white"
}
const _hoisted_5 = {
  key: 0,
  class: "m-auto grid grid-cols-[repeat(auto-fill,_minmax(13rem,_1fr))] gap-2 sm:gap-4 py-1 sm:py-2"
}
const _hoisted_6 = ["href"]
const _hoisted_7 = {
  key: 1,
  class: "m-auto grid grid-cols-[repeat(auto-fill,_minmax(13rem,_1fr))] gap-2 sm:gap-4 py-1 sm:py-2"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "min-w-[13rem] w-full min-h-[calc(13rem+1.75rem)] aspect-square bg-gray-300" }, null, -1)
const _hoisted_9 = [
  _hoisted_8
]
const _hoisted_10 = {
  key: 2,
  class: "flex items-center justify-center h-full"
}
const _hoisted_11 = { class: "bg-white rounded-md py-1 sm:py-2 px-2 sm:px-4" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("hr", { class: "mt-2 sm:mt-4" }, null, -1)
const _hoisted_13 = { class: "mt-2 sm:mt-4 -mb-6" }

import { Empty, Input, Modal, Pagination, TreeSelectProps } from 'ant-design-vue';
import { createVNode, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
	DeleteFilled,
	ExportOutlined,
	EyeFilled,
	PlusOutlined,
	StarFilled
} from '@ant-design/icons-vue';
import AdvanceFilter from '@/components/disposable/AdvanceFilter.vue';
import DoujinCard from '@/components/disposable/DoujinCard.vue';
import SortDropdown from '@/components/disposable/SortDropdown.vue';
import ActionButtons from '@/components/shared/ActionButtons.vue';
import { Metadata } from '@/model/api';
import { ActionButton } from '@/model/button';
import { RATING_SCORE_MAXIMUM } from '@/model/doujin';
import { OrderOption, SortOption } from '@/model/dropdown';
import { Doujin, DoujinPaginationRequest } from '@/model/doujin';
import LocalStorageKey from '@/model/key';
import { ActionMode, SortSequence } from '@/model/universal';
import {
	deleteDoujin,
	favoriteDoujin,
	getDoujinOption,
	getDoujinsPagination
} from '@/repository/doujin';
import { assignWithDetail } from '@/util/universal';

const External = 'external';
enum FilterID {
	Artists = 'artists',
	Characters = 'characters',
	Tags = 'tags',
	Favorite = 'Favorite'
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DoujinView',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const store = useStore();

// Sort Option
const sort = ref('');
const sortOptions: SortOption[] = [
	{
		label: 'Date',
		value: 'created_at'
	},
	{
		label: 'Title',
		value: 'title'
	},
	{
		label: 'Artist',
		value: 'artists.0'
	},
	{
		label: 'Favorite',
		value: 'is_favorite'
	},
	{
		label: 'Rating',
		value: 'your_rating'
	}
];
const orderOptions = ref<OrderOption[]>([
	{
		label: (sortBy: string) => {
			switch (sortBy) {
				case 'created_at':
					return 'First to last';
				case 'title':
				case 'artists.0':
					return '0-9, A-Z, a-z';
				case 'is_favorite':
					return 'Favorite last';
				case 'your_rating':
					return `0 to ${RATING_SCORE_MAXIMUM}`;
				default:
					return '';
			}
		},
		value: SortSequence.Ascending
	},
	{
		label: (sortBy: string) => {
			switch (sortBy) {
				case 'created_at':
					return 'Last to first';
				case 'title':
				case 'artists.0':
					return 'z-a, Z-A, 9-0';
				case 'is_favorite':
					return 'Favorite first';
				case 'your_rating':
					return `${RATING_SCORE_MAXIMUM} to 0`;
				default:
					return '';
			}
		},
		value: SortSequence.Descending
	}
]);

// Action Buttons
const mode = ref(
	assignWithDetail<string>(
		[ActionMode.View, External, ActionMode.Favorite, ActionMode.Add, ActionMode.Delete],
		ActionMode.View,
		localStorage.getItem(LocalStorageKey.DoujinAction)
	)
);

const imageLoadingMode = ref(
	assignWithDetail<'lazy' | 'eager'>(
		['lazy', 'eager'],
		'lazy',
		localStorage.getItem(LocalStorageKey.ImageLoading) as 'lazy' | 'eager'
	)
);

const actions: ActionButton[][] = [
	[
		{
			tooltip: 'View',
			mode: ActionMode.View,
			component: EyeFilled,
			activeClass: 'bg-pink-400 !text-black',
			hoverClass: 'hover:bg-pink-400 hover:!text-black',
			action: () => (mode.value = ActionMode.View)
		},
		{
			tooltip: 'External Link',
			mode: External,
			component: ExportOutlined,
			activeClass: 'bg-blue-400 !text-black',
			hoverClass: 'hover:bg-blue-400 hover:!text-black',
			action: () => (mode.value = External)
		},
		{
			tooltip: 'Favorite',
			mode: ActionMode.Favorite,
			component: StarFilled,
			activeClass: 'bg-yellow-400 !text-red-600',
			hoverClass: 'hover:bg-yellow-400 hover:!text-red-600',
			action: () => (mode.value = ActionMode.Favorite)
		}
	],
	[
		{
			tooltip: 'Add',
			mode: ActionMode.Add,
			component: PlusOutlined,
			activeClass: 'bg-green-300 !text-black',
			hoverClass: 'hover:bg-green-300 hover:!text-black',
			action: () => router.push({ name: 'Create Doujin' })
		},
		{
			tooltip: 'Delete',
			mode: ActionMode.Delete,
			component: DeleteFilled,
			activeClass: 'bg-red-500 !text-white',
			hoverClass: 'hover:bg-red-500 hover:!text-white',
			action: () => (mode.value = ActionMode.Delete)
		}
	]
];

// Advance Filter
const filterBy = ref<string[]>(
	(() => {
		const filters: string[] = [];
		for (const filterID of Object.values(FilterID)) {
			const filter =
				route.query[filterID]
					?.toString()
					.split(',')
					.map((query) => filterID + ':' + query) || [];
			filters.push(...filter);
		}
		const isFavorite = route.query.isFavorite ? [FilterID.Favorite] : [];
		return [...isFavorite, ...filters];
	})()
);
const filters = ref<TreeSelectProps['treeData']>([
	{
		label: 'Favorite',
		value: 'Favorite',
		color: 'yellow'
	},
	{
		label: 'Artists',
		value: 'Artists',
		color: 'cyan',
		children: []
	},
	{
		label: 'Characters',
		value: 'Characters',
		color: 'green',
		children: []
	},
	{
		label: 'Tags',
		value: 'Tags',
		color: 'pink',
		children: []
	}
]);
function clearInvalidFilter() {
	if (filterBy.value && filters.value) {
		let allFilters: string[] = [];
		filters.value.forEach((filter) => {
			if (filter.children) {
				allFilters.push(...filter.children.map((f) => f.value as string));
			} else {
				allFilters.push(filter.value);
			}
		});
		filterBy.value = filterBy.value.filter((filter) => allFilters.includes(filter));
	}
}

// Specific Data
let doujins = ref<Doujin[]>([]);
const isFetching = ref(true);
const search = ref(route.query.search?.toString() || '');
const metadata = ref<Metadata>(initialMetadata());
function initialMetadata(): Metadata {
	return {
		limit: Number(route.query.limit) || 10,
		offset: 0,
		totalItem: 0,
		totalPage: 0,
		currentPage: Number(route.query.page) || 1
	};
}

onMounted(async () => {
	const options = await getDoujinOption();
	if (options && filters.value) {
		const option = (value: string[], color: string, id: string) =>
			value.map((v) => {
				return {
					label: v,
					value: id + ':' + v,
					color
				};
			});
		filters.value[1].children = option(options.artists, 'cyan', FilterID.Artists);
		filters.value[2].children = option(options.characters, 'green', FilterID.Characters);
		filters.value[3].children = option(options.tags, 'pink', FilterID.Tags);
	}
	clearInvalidFilter();
});

function filter(field: string) {
	if (field === FilterID.Favorite) {
		return filterBy.value.findIndex((filter) => filter === FilterID.Favorite) !== -1
			? FilterID.Favorite
			: '';
	}
	const filterValues = filterBy.value.filter((f) => f !== FilterID.Favorite);
	const filterByField =
		filters.value
			?.find((filter) => filter.label.toLowerCase() === field)
			?.children?.map((v) => v.value) || [];
	return filterByField
		.filter((filter) => filterValues.includes(filter))
		.map((filter) => filter.substring(filter.indexOf(':') + 1))
		.join(',');
}

async function fetchData() {
	let multipleSort = sort.value;
	if (sort.value && !sort.value.includes('created_at')) {
		multipleSort += `,created_at ${sort.value.split(' ')[1]}`;
	}
	const advanceFilter = {
		artists: filter(FilterID.Artists) || undefined,
		characters: filter(FilterID.Characters) || undefined,
		tags: filter(FilterID.Tags) || undefined,
		isFavorite: filter(FilterID.Favorite).length > 0 || undefined,
		source: route.query.source?.toString() || undefined,
		code: route.query.code?.toString() || undefined
	};
	const query: DoujinPaginationRequest = {
		page: metadata.value.currentPage,
		offset: metadata.value.offset,
		limit: metadata.value.limit,
		sort: multipleSort,
		search: search.value,
		characters: advanceFilter.characters,
		artists: advanceFilter.artists,
		tags: advanceFilter.tags,
		source: advanceFilter.source,
		code: advanceFilter.code,
		isFavorite: advanceFilter.isFavorite
	};
	router.replace({
		query: {
			page: metadata.value.currentPage,
			limit: metadata.value.limit,
			search: search.value || undefined,
			sort:
				sortOptions
					.find((s) => s.value.toLowerCase() === sort.value.split(' ')[0].toLowerCase())
					?.label?.toLowerCase() || undefined,
			order: sort.value.split(' ')[1] || undefined,
			artists: advanceFilter.artists,
			characters: advanceFilter.characters,
			tags: advanceFilter.tags,
			source: advanceFilter.source,
			code: advanceFilter.code,
			isFavorite: advanceFilter.isFavorite ? 'true' : undefined
		}
	});
	try {
		isFetching.value = true;
		const data = await getDoujinsPagination(query);
		if (data) {
			isFetching.value = false;
			doujins.value = data.data;
			metadata.value = data.metadata;
			if (metadata.value.totalPage > 0 && (!data.data || !data.data.length)) {
				metadata.value.currentPage = metadata.value.totalPage;
			}
		}
	} catch (error) {
		isFetching.value = false;
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

async function clickCard(doujin: Doujin) {
	switch (mode.value) {
		case ActionMode.View:
			router.push(`/doujin/${doujin.doujinUUID}`);
			return;

		case External:
			window.open(doujin.link, '_blank');
			return;

		case ActionMode.Favorite:
			doujin.isFavorite = !doujin.isFavorite;
			await favoriteDoujin(doujin.doujinUUID as string, doujin.isFavorite);
			return;

		case ActionMode.Delete:
			Modal.confirm({
				title: 'Delete Doujin',
				icon: createVNode(DeleteFilled),
				content: 'Do you want to delete this doujin right?',
				okText: 'Yes',
				cancelText: 'No',
				async onOk() {
					if (doujin.doujinUUID) {
						await deleteDoujin(doujin.doujinUUID);
						await fetchData();
					}
				}
			});
			return;
	}
}

watch(mode, () => localStorage.setItem(LocalStorageKey.DoujinAction, mode.value));
watch(
	() => metadata.value.currentPage,
	async () => (metadata?.value.currentPage !== 0 ? await fetchData() : {})
);
watch(() => metadata.value.limit, fetchData);
watch(() => filterBy.value, fetchData);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(SortDropdown, {
        sortOptions: sortOptions,
        orderOptions: orderOptions.value,
        defaultSortBy: sortOptions[0].value,
        defaultOrderBy: orderOptions.value[1].value,
        onSort: _cache[0] || (_cache[0] = (value) => (sort.value = value)),
        onChange: fetchData
      }, null, 8, ["orderOptions", "defaultSortBy", "defaultOrderBy"]),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(actions, (buttons, index) => {
          return _createElementVNode("div", {
            key: index,
            class: "flex items-center gap-2 sm:gap-4 my-2 sm:my-4"
          }, [
            _createVNode(ActionButtons, {
              actions: buttons,
              mode: mode.value,
              placement: 
							(buttonIndex) =>
								index === actions.length - 1 && buttonIndex === buttons.length - 1
									? 'topRight'
									: !index && !buttonIndex
										? 'topLeft'
										: 'top'
						
            }, null, 8, ["actions", "mode", "placement"]),
            (index !== actions.length - 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4))
              : _createCommentVNode("", true)
          ])
        }), 64))
      ])
    ]),
    _createVNode(_unref(Input).Search, {
      value: search.value,
      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
      placeholder: "Search doujin...",
      "enter-button": "",
      "allow-clear": "",
      onSearch: fetchData
    }, null, 8, ["value"]),
    _createElementVNode("div", {
      class: _normalizeClass(
				'py-1 sm:py-2 ' +
				(isFetching.value || _unref(doujins)?.length
					? 'min-h-[calc(100vh-80px-208px)] max-[575px]:min-h-[calc(100vh-80px-248px)] sm:min-h-[calc(100vh-80px-264px)]'
					: 'h-[calc(100vh-80px-208px)] max-[575px]:h-[calc(100vh-80px-248px)] sm:h-[calc(100vh-80px-264px)]')
			)
    }, [
      (!isFetching.value && _unref(doujins)?.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(doujins), (doujin, index) => {
              return (_openBlock(), _createElementBlock("a", {
                key: index,
                href: mode.value === External ? doujin.link : `/doujin/${doujin.doujinUUID}`,
                onclick: "return false"
              }, [
                _createVNode(DoujinCard, {
                  doujin: doujin,
                  "image-loading": imageLoadingMode.value,
                  onClick: clickCard
                }, null, 8, ["doujin", "image-loading"])
              ], 8, _hoisted_6))
            }), 128))
          ]))
        : (isFetching.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Array(metadata.value.limit), (_, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "rounded-md shadow-md bg-white overflow-hidden animate-pulse"
                }, _hoisted_9))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createVNode(_unref(Empty))
            ]))
    ], 2),
    _createElementVNode("div", _hoisted_11, [
      _createVNode(_unref(Pagination), {
        current: metadata.value.currentPage,
        "onUpdate:current": _cache[2] || (_cache[2] = ($event: any) => ((metadata.value.currentPage) = $event)),
        "page-size": metadata.value.limit,
        "onUpdate:pageSize": _cache[3] || (_cache[3] = ($event: any) => ((metadata.value.limit) = $event)),
        total: metadata.value.totalItem,
        "show-total": (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        "show-size-changer": "",
        "show-quick-jumper": "",
        onChange: _cache[4] || (_cache[4] = 
					(page, pageSize) => {
						metadata.value.currentPage = page;
						metadata.value.limit = pageSize;
					}
				)
      }, null, 8, ["current", "page-size", "total", "show-total"])
    ]),
    _hoisted_12,
    _createElementVNode("div", _hoisted_13, [
      _createVNode(AdvanceFilter, {
        filterBy: filterBy.value,
        filters: filters.value,
        favoriteID: FilterID.Favorite,
        filterIDs: Object.values(FilterID),
        onFilter: _cache[5] || (_cache[5] = (filter) => (filterBy.value = filter))
      }, null, 8, ["filterBy", "filters", "favoriteID", "filterIDs"])
    ])
  ]))
}
}

})